import { config } from '../config';
import { auth } from '../config/firebase';
import { SubscriptionData, Invoice } from '../types/subscription';

const BILLING_API_URL = config.billing.baseURL;

/**
 * Get Firebase auth token
 */
const getAuthToken = async (): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return await user.getIdToken();
};

/**
 * Fetch subscription details
 */
export const getSubscription = async (): Promise<SubscriptionData> => {
  const token = await getAuthToken();

  const response = await fetch(
    `${BILLING_API_URL}/api/billing/subscription`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch subscription');
  }

  const data = await response.json();
  return data.data;
};

/**
 * Create Stripe checkout session
 */
export const createCheckoutSession = async (
  email: string,
  additionalUsers: number = 0
): Promise<{ sessionId: string; checkoutUrl: string }> => {
  const token = await getAuthToken();

  const successUrl = `${window.location.origin}/subscription?session_id={CHECKOUT_SESSION_ID}`;
  const cancelUrl = `${window.location.origin}/subscription`;

  const response = await fetch(`${BILLING_API_URL}/api/billing/checkout-session`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
      successUrl,
      cancelUrl,
      additionalUsers,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create checkout session');
  }

  const data = await response.json();
  return data.data;
};

/**
 * Update subscription user seats
 */
export const updateSubscriptionSeats = async (
  additionalUsers: number,
  usersToDeactivate?: Array<{ userId: string; email: string; firebaseUid: string }>
): Promise<any> => {
  const token = await getAuthToken();

  const response = await fetch(`${BILLING_API_URL}/api/billing/update-seats`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      additionalUsers,
      usersToDeactivate: usersToDeactivate || [],
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update subscription seats');
  }

  return await response.json();
};

/**
 * Cancel subscription (always at period end)
 */
export const cancelSubscription = async (
  reason?: string
): Promise<void> => {
  const token = await getAuthToken();

  const response = await fetch(`${BILLING_API_URL}/api/billing/cancel`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      reason,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to cancel subscription');
  }
};

/**
 * Sync with Stripe
 */
export const syncWithStripe = async (): Promise<void> => {
  console.log('Calling syncWithStripe...');
  const token = await getAuthToken();

  const response = await fetch(`${BILLING_API_URL}/api/billing/sync`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const error = await response.json();
    console.error('syncWithStripe failed:', error);
    throw new Error(error.message || 'Failed to sync with Stripe');
  }

  const data = await response.json();
  console.log('syncWithStripe response:', data);
};

/**
 * Get billing history
 */
export const getBillingHistory = async (
  limit: number = 10
): Promise<Invoice[]> => {
  const token = await getAuthToken();

  const response = await fetch(
    `${BILLING_API_URL}/api/billing/history?limit=${limit}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch billing history');
  }

  const data = await response.json();
  return data.data.history;
};

/**
 * Verify checkout session completion
 */
export const verifyCheckoutSession = async (
  sessionId: string
): Promise<void> => {
  console.log('Calling verifyCheckoutSession with sessionId:', sessionId);
  const token = await getAuthToken();

  const response = await fetch(`${BILLING_API_URL}/api/billing/verify-session`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sessionId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    console.error('verifyCheckoutSession failed:', error);
    throw new Error(error.message || 'Failed to verify checkout session');
  }

  const data = await response.json();
  console.log('verifyCheckoutSession response:', data);
};

/**
 * Increment used user seats (call when adding a user)
 */
export const incrementUsedSeats = async (): Promise<void> => {
  const token = await getAuthToken();

  const response = await fetch(`${BILLING_API_URL}/api/billing/increment-used-seats`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to increment used seats');
  }
};

/**
 * Decrement used user seats (call when removing a user)
 */
export const decrementUsedSeats = async (): Promise<void> => {
  const token = await getAuthToken();

  const response = await fetch(`${BILLING_API_URL}/api/billing/decrement-used-seats`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to decrement used seats');
  }
};

/**
 * Get pending changes (seat reductions scheduled for period end)
 */
export const getPendingChanges = async (): Promise<any> => {
  const token = await getAuthToken();

  const response = await fetch(`${BILLING_API_URL}/api/billing/pending-changes`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch pending changes');
  }

  const data = await response.json();
  return data.data;
};

/**
 * Create a Stripe Billing Portal session for managing payment methods.
 * Returns the portal URL to redirect the user to.
 */
export const createBillingPortalSession = async (): Promise<{ url: string }> => {
  const token = await getAuthToken();
  const returnUrl = `${window.location.origin}/subscription`;

  const response = await fetch(`${BILLING_API_URL}/api/billing/billing-portal`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ returnUrl }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create billing portal session');
  }

  const data = await response.json();
  return data.data;
};

/**
 * Cancel pending seat reduction
 */
export const cancelPendingReduction = async (): Promise<void> => {
  const token = await getAuthToken();

  const response = await fetch(`${BILLING_API_URL}/api/billing/cancel-pending-reduction`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to cancel pending reduction');
  }
};
